import { useEffect, useMemo, useState } from 'react';

import { usePracticeProfile } from 'pages/Dashboard/Billing/hooks';
import { getLastBulkShippingInfo } from 'store/groups/service';
import { getDefaultAddress, isAddressValid, shippingAddressToForm } from 'utils/Address';

import { IShippingInfo } from './Onboard';

const fromLastOrder = (last: IDeviceOrderShippingInfo): IShippingInfo => ({
  firstName: last.firstName,
  lastName: last.lastName,
  email: last.email || '',
  phoneNumber: last.phoneNumber || '',
  address: shippingAddressToForm(last.address)
});

const fromPractice = (profile: IBillingStatus): IShippingInfo => {
  const [ownerFirst, ...ownerRest] = (profile.ownerName ?? '').trim().split(' ');
  const practice = profile.practiceAddress;
  return {
    firstName: ownerFirst,
    lastName: ownerRest.join(' '),
    email: profile.ownerEmail || '',
    phoneNumber: '',
    address: getDefaultAddress(
      practice && {
        street_address_line_1: practice.line1,
        street_address_line_2: practice.line2 ?? '',
        city: practice.city,
        state_province: practice.region,
        postal_code: practice.postalCode,
        country_code: practice.country
      }
    )
  };
};

/** Undefined while loading, null when the group has no bulk order yet or the call failed. */
const useLastBulkShippingInfo = (gid: string): IDeviceOrderShippingInfo | null | undefined => {
  const [last, setLast] = useState<IDeviceOrderShippingInfo | null | undefined>(undefined);

  useEffect(() => {
    if (!gid) {
      setLast(null);
      return () => undefined;
    }
    setLast(undefined);
    let current = true;
    getLastBulkShippingInfo(gid)
      .catch(() => null)
      .then((info) => current && setLast(info));
    return () => {
      current = false;
    };
  }, [gid]);

  return last;
};

/**
 * Where a stock order ships by default: wherever the group's last one went, else the practice
 * address and account owner from signup. Null until that is known, or when neither is.
 */
export const useStockOrderShippingDefaults = (gid: string): IShippingInfo | null => {
  const last = useLastBulkShippingInfo(gid);
  const profile = usePracticeProfile(gid);

  return useMemo(() => {
    if (last === undefined) return null;
    const fromLast = last && fromLastOrder(last);
    if (fromLast && isAddressValid(fromLast.address)) return fromLast;
    return profile ? fromPractice(profile) : null;
  }, [last, profile]);
};
